import React, { useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import { FinalChunkRenderer } from "./finalChunk";

export type StreamChunk = {
  step: number;
  step_title: string;
  chunk_id?: number;
  chunk_text?: string | object;
};

type Props = {
  chunks: StreamChunk[];
  currentStepTitle?: string;
};

export const StepAccordionStream: React.FC<Props> = ({ chunks, currentStepTitle }) => {
  const { groupedSteps, finalStepRawText } = useMemo(() => {
    const stepMap = new Map<
      number,
      { title: string; chunks: (string | object)[] }
    >();
    let maxStep = -1;

    for (const chunk of chunks) {
      maxStep = Math.max(maxStep, chunk.step);
      const entry = stepMap.get(chunk.step) ?? {
        title: chunk.step_title,
        chunks: [],
      };
      entry.title = chunk.step_title;
      if (chunk.chunk_text !== undefined) {
        entry.chunks.push(chunk.chunk_text);
      }
      stepMap.set(chunk.step, entry);
    }

    const groupedSteps: {
      step: number;
      title: string;
      text: string;
      hasContent: boolean;
    }[] = [];
    let finalStepRawText = "";

    const sortedEntries = Array.from(stepMap.entries()).sort(
      ([a], [b]) => a - b
    );

    for (const [step, data] of sortedEntries) {
      const combined = data.chunks
        .map((chunk) =>
          typeof chunk === "string" ? chunk : JSON.stringify(chunk, null, 2)
        )
        .join("");

      if (step === maxStep) {
        finalStepRawText = combined;
      } else {
        groupedSteps.push({
          step,
          title: data.title,
          text: combined,
          hasContent: combined.trim().length > 0,
        });
      }
    }

    return { groupedSteps, finalStepRawText };
  }, [chunks]);

  return (
    <Box>
      {groupedSteps.map(({ step, title, text, hasContent }) => (
        <Accordion key={step}>
          <AccordionSummary expandIcon={hasContent ? <ExpandMoreIcon /> : undefined}>
            <Typography fontWeight="bold">{title}</Typography>
          </AccordionSummary>
          {hasContent && (
            <AccordionDetails>
              <ReactMarkdown
                rehypePlugins={[[rehypeHighlight as any]]}
              >
                {text}
              </ReactMarkdown>
            </AccordionDetails>
          )}
        </Accordion>
      ))}

      {finalStepRawText.trim().length > 0 && (
        <FinalChunkRenderer markdown={finalStepRawText} currentStepTitle={currentStepTitle}/>
      )}
    </Box>
  );
};